import { useState, useCallback } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export interface PaymentRun {
  id: string;
  name: string;
  budget: number;
  pay_date: string;
  status: "draft" | "processing" | "completed";
  created_by: string | null;
  created_at: string;
  updated_at: string;
  week_start?: string | null;
  auto_generated?: boolean;
  eligible_savings?: number | null;
}

export interface PaymentRunInvoice {
  id: string;
  run_id: string;
  vendor_name: string;
  invoice_number: string | null;
  outstanding: number;
  potential_savings: number;
  early_pay_due_date: string | null;
  included: boolean;
}

export interface NewRunInvoice {
  vendor_name: string;
  invoice_number: string | null;
  outstanding: number;
  potential_savings: number;
  early_pay_due_date: string | null;
}

export function usePaymentRuns() {
  const { toast } = useToast();
  const { companyId } = useAuth();
  const [runs, setRuns] = useState<PaymentRun[]>([]);
  const [loading, setLoading] = useState(true); // true = "not yet fetched"; cleared after first fetchRuns()

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("payment_runs")
      .select("*")
      .order("pay_date", { ascending: true })
      .order("created_at", { ascending: true });
    setLoading(false);
    if (error || !data) return;
    setRuns(data as PaymentRun[]);
  }, []);

  /** Create a run and auto-populate with the given invoices (already greedy-selected by caller). */
  const createRun = useCallback(
    async (
      budget: number,
      payDate: string,
      invoices: NewRunInvoice[],
      createdBy: string | null,
      eligibleSavings?: number
    ): Promise<PaymentRun | null> => {
      const name = `${format(new Date(payDate + "T00:00:00"), "MMM d")} Run – ${
        budget >= 1_000_000
          ? `$${(budget / 1_000_000).toFixed(1)}M`
          : budget >= 1_000
            ? `$${Math.round(budget / 1_000)}k`
            : `$${Math.round(budget)}`
      }`;

      const { data: run, error } = await supabase
        .from("payment_runs")
        .insert({
          name,
          budget,
          pay_date: payDate,
          status: "draft",
          created_by: createdBy,
          company_id: companyId,
          ...(eligibleSavings !== undefined ? { eligible_savings: eligibleSavings } : {}),
        })
        .select()
        .single();

      if (error || !run) {
        toast({
          title: "Failed to create payment run",
          description: error?.message,
          variant: "destructive",
        });
        return null;
      }

      if (invoices.length > 0) {
        const { error: itemsError } = await supabase.from("payment_run_invoices").insert(
          invoices.map((inv) => ({
            run_id: run.id,
            vendor_name: inv.vendor_name,
            invoice_number: inv.invoice_number,
            outstanding: inv.outstanding,
            potential_savings: inv.potential_savings,
            early_pay_due_date: inv.early_pay_due_date,
            included: true,
          }))
        );
        if (itemsError) {
          toast({
            title: "Run created but failed to save invoices",
            description: itemsError.message,
            variant: "destructive",
          });
        }
      }

      await fetchRuns();
      return run as PaymentRun;
    },
    [fetchRuns, toast]
  );

  const updateRun = useCallback(
    async (id: string, updates: Partial<Pick<PaymentRun, "status" | "eligible_savings">>) => {
      const { error } = await supabase
        .from("payment_runs")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) {
        toast({
          title: "Failed to update run",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      await fetchRuns();
    },
    [fetchRuns, toast]
  );

  /** Save (create or update) a run with name, pay_date, budget and optionally replace its invoices. */
  const saveRun = useCallback(
    async (params: {
      id?: string | null;
      name: string;
      pay_date: string;
      budget: number;
      invoices?: NewRunInvoice[];
      createdBy?: string | null;
    }): Promise<PaymentRun | null> => {
      const { id, name, pay_date, budget, invoices, createdBy } = params;

      let run: PaymentRun | null = null;

      if (id) {
        // Update existing run
        const { data, error } = await supabase
          .from("payment_runs")
          .update({ name, pay_date, budget, updated_at: new Date().toISOString() })
          .eq("id", id)
          .select()
          .single();
        if (error || !data) {
          toast({
            title: "Failed to update payment run",
            description: error?.message,
            variant: "destructive",
          });
          return null;
        }
        run = data as PaymentRun;
      } else {
        // Create new run
        const { data, error } = await supabase
          .from("payment_runs")
          .insert({
            name,
            pay_date,
            budget,
            status: "draft",
            created_by: createdBy ?? null,
            company_id: companyId,
          })
          .select()
          .single();
        if (error || !data) {
          toast({
            title: "Failed to create payment run",
            description: error?.message,
            variant: "destructive",
          });
          return null;
        }
        run = data as PaymentRun;
      }

      // Replace invoices if provided
      if (run && invoices !== undefined) {
        await supabase.from("payment_run_invoices").delete().eq("run_id", run.id);
        if (invoices.length > 0) {
          const { error: itemsError } = await supabase.from("payment_run_invoices").insert(
            invoices.map((inv) => ({
              run_id: run!.id,
              vendor_name: inv.vendor_name,
              invoice_number: inv.invoice_number,
              outstanding: inv.outstanding,
              potential_savings: inv.potential_savings,
              early_pay_due_date: inv.early_pay_due_date,
              included: true,
            }))
          );
          if (itemsError) {
            toast({
              title: "Run saved but failed to update invoices",
              description: itemsError.message,
              variant: "destructive",
            });
          }
        }
      }

      await fetchRuns();
      return run;
    },
    [fetchRuns, toast]
  );

  /** Fetch a single run by ID. */
  const getRun = useCallback(async (id: string): Promise<PaymentRun | null> => {
    const { data, error } = await supabase.from("payment_runs").select("*").eq("id", id).single();
    if (error || !data) return null;
    return data as PaymentRun;
  }, []);

  const deleteRun = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("payment_runs").delete().eq("id", id);
      if (error) {
        toast({
          title: "Failed to delete run",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      setRuns((prev) => prev.filter((r) => r.id !== id));
    },
    [toast]
  );

  return { runs, loading, fetchRuns, createRun, updateRun, saveRun, getRun, deleteRun };
}

export function usePaymentRunInvoices(runId: string | null) {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<PaymentRunInvoice[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("payment_run_invoices")
      .select("*")
      .eq("run_id", runId)
      .order("vendor_name", { ascending: true });
    setLoading(false);
    if (!error && data) setInvoices(data as PaymentRunInvoice[]);
  }, [runId]);

  const updateInvoiceInclusion = useCallback(
    async (invoiceId: string, included: boolean) => {
      const { error } = await supabase
        .from("payment_run_invoices")
        .update({ included })
        .eq("id", invoiceId);
      if (error) {
        toast({
          title: "Failed to update invoice",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      setInvoices((prev) => prev.map((inv) => (inv.id === invoiceId ? { ...inv, included } : inv)));
    },
    [toast]
  );

  /** Replace all invoices in this run (used by Re-optimize). */
  const replaceInvoices = useCallback(
    async (newInvoices: NewRunInvoice[], eligibleSavings?: number) => {
      if (!runId) return;
      // Delete existing
      await supabase.from("payment_run_invoices").delete().eq("run_id", runId);
      // Insert new
      if (newInvoices.length > 0) {
        const { error } = await supabase.from("payment_run_invoices").insert(
          newInvoices.map((inv) => ({
            run_id: runId,
            vendor_name: inv.vendor_name,
            invoice_number: inv.invoice_number,
            outstanding: inv.outstanding,
            potential_savings: inv.potential_savings,
            early_pay_due_date: inv.early_pay_due_date,
            included: true,
          }))
        );
        if (error) {
          toast({
            title: "Failed to replace invoices",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }
      // Update eligible_savings on the run if provided
      if (eligibleSavings !== undefined) {
        await supabase
          .from("payment_runs")
          .update({ eligible_savings: eligibleSavings, updated_at: new Date().toISOString() })
          .eq("id", runId);
      }
      await fetchInvoices();
    },
    [runId, fetchInvoices, toast]
  );

  return { invoices, loading, fetchInvoices, updateInvoiceInclusion, replaceInvoices };
}
